import React, { useState } from "react";
import Autocomplete from "react-google-autocomplete";
import {
  Box,
  Button,
  Link,
  Typography,
  TextField,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { ArrowRight } from "lucide-react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";
import sh from "bundles/common/utils/sh";
import { useSnackbar } from "notistack";
import {
  companyIndustryCreateMap,
  companySubSectorMap,
  getResponseMessageId,
} from "bundles/common/utils/constants";
import { normalizeRegion } from "bundles/common/utils/utils";

// Event snippet for Inscription Client SH conversion page In your html page, add the snippet and call gtag_report_conversion when someone clicks on the chosen link or button.
function gtag_report_conversion(url) {
  var callback = function () {
    if (typeof url != "undefined") {
      window.location = url;
    }
  };
  gtag("event", "conversion", {
    send_to: "AW-10868281309/HoiQCI_m_asDEN2fs74o",
    event_callback: callback,
  });
  return false;
}

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "0.5rem",
    fontFamily: "Segoe UI Regular, system-ui, sans-serif",
    fontSize: "0.875rem",
    "& fieldset": { borderColor: "hsl(220, 13%, 91%)" },
    "&:hover fieldset": { borderColor: "hsl(220, 13%, 80%)" },
    "&.Mui-focused fieldset": {
      border: "2px solid hsl(153 68% 25%)",
    },
  },
  "& .MuiOutlinedInput-input": {
    color: "hsl(220, 25%, 12%)",
  },
  "& .MuiInputLabel-root": {
    color: "hsl(220, 10%, 46%)",
    fontFamily: "Segoe UI Regular, system-ui, sans-serif",
    fontSize: "0.875rem",
    "&.Mui-focused": {
      color: "hsl(153 68% 25%)",
    },
  },
};

const selectSx = {
  borderRadius: "0.5rem",
  fontFamily: "Segoe UI Regular, system-ui, sans-serif",
  fontSize: "0.875rem",
  "& fieldset": { borderColor: "hsl(220, 13%, 91%)" },
  "&:hover fieldset": { borderColor: "hsl(220, 13%, 80%)" },
  "&.Mui-focused fieldset": {
    border: "2px solid hsl(153 68% 25%)",
  },
};

export default function StepThree({
  recruiterData,
  setRecruiterData,
  setCompanyData,
  nextStep,
}) {
  const i18nLocale = useSelector((state) => state.i18nLocale);
  const railsEnv = useSelector((state) => state.railsEnv);
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();

  const initialFormValues = {
    name: "",
    job_title: "",
    company_industry: null,
    sub_sector: null,
    location: null,
  };

  const [values, setValues] = useState(initialFormValues);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const validate = (fieldValues = values) => {
    let temp = { ...errors };
    let required = (
      <FormattedMessage id="form_validation.required"></FormattedMessage>
    );

    if ("name" in fieldValues) {
      temp.name = fieldValues.name ? "" : required;
    }

    if ("job_title" in fieldValues) {
      temp.job_title = fieldValues.job_title ? "" : required;
    }

    if ("location" in fieldValues) {
      temp.location = fieldValues.location ? "" : required;
    }

    if ("company_industry" in fieldValues) {
      temp.company_industry = fieldValues.company_industry ? "" : required;
    }

    setErrors({
      ...temp,
    });

    return Object.values(temp).every((x) => x === "");
  };

  const handleInputValue = (e) => {
    const { name, value } = e.target;
    setValues({
      ...values,
      [name]: value,
    });
    validate({ [name]: value });
  };

  const onPlacesSelect = (place) => {
    if (place.address_components) {
      let compPostalCode = "";
      let compLocality = "";
      let compAdministrativeAreaLevel2 = "";
      let compAdministrativeAreaLevel1 = "";
      let countryShortName = "";
      let regionValue = "";

      place.address_components.forEach((c) => {
        if (c.types.includes("postal_code")) {
          compPostalCode = c.long_name;
        }
        if (c.types.includes("locality")) {
          compLocality = c.long_name;
        }
        if (c.types.includes("administrative_area_level_2")) {
          compAdministrativeAreaLevel2 = c.long_name
            ?.toLowerCase()
            ?.replace(/ /g, "_");
        }
        if (c.types.includes("administrative_area_level_1")) {
          compAdministrativeAreaLevel1 = c.long_name
            ?.toLowerCase()
            ?.replace(/ /g, "_");
        }
        if (c.types.includes("country")) {
          countryShortName = c.short_name.toLowerCase();
        }
      });

      if (countryShortName === "fr") {
        regionValue = compAdministrativeAreaLevel1 || compAdministrativeAreaLevel2;
        regionValue = normalizeRegion(regionValue);
      } else if (countryShortName === "be") {
        if (compAdministrativeAreaLevel2 === "luxembourg") {
          regionValue = "be_luxembourg";
        } else {
          regionValue = compAdministrativeAreaLevel2 || compAdministrativeAreaLevel1;
        }
      } else if (countryShortName === "lu") {
        regionValue = compAdministrativeAreaLevel1;
      } else {
        regionValue = compAdministrativeAreaLevel2 || compAdministrativeAreaLevel1;
      }

      let newValues = {
        ...values,
        location: place.formatted_address,
        zipcode: compPostalCode,
        city: compLocality,
        region: regionValue,
        country: countryShortName,
      };

      setValues(newValues);
    }
  };


  const handleSubmit = (e) => {
    setLoading(true);
    e.preventDefault();
    if (validate(values)) {
      setRecruiterData({ ...recruiterData, job_title: values["job_title"] });
      setCompanyData({
        name: values["name"],
        company_industry: values["company_industry"],
        sub_sector: values["sub_sector"],
        zipcode: values["zipcode"],
        city: values["city"],
        region: values["region"],
        country: values["country"],
      });
      createCompany();
    } else {
      setLoading(false);
    }
  };

  const createCompany = () => {
    if (validate(values)) {
      sh.post(`${i18nLocale}/recruiters`, {
        recruiter: { ...recruiterData, job_title: values["job_title"] },
        company: {
          name: values["name"],
          company_industry: values["company_industry"],
          sub_sector: values["sub_sector"],
          zipcode: values["zipcode"],
          city: values["city"],
          region: values["region"],
          country: values["country"],
        },
      })
        .then((res) => {
          if (typeof fbq !== "undefined")
            fbq("trackCustom", "Inscription Recruteur SH");
          if (window && typeof window.lintrk !== "undefined")
            window.lintrk("track", { conversion_id: 8587650 });
          if (railsEnv === "production" && typeof gtag !== "undefined")
            gtag_report_conversion();

          setCompanyData({ name: values["name"], id: res?.data?.company_id });
          nextStep();
        })
        .catch((err) => {
          setLoading(false);
          const data = err.response?.data;
          if (data?.errors?.length) {
            data.errors.forEach((error) => {
              enqueueSnackbar(error, { variant: "error" });
            });
          } else if (data?.message) {
            enqueueSnackbar(
              getResponseMessageId(data.message),
              { variant: "error" }
            );
          }
          console.error("Error", err);
        });
    } else {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        bgcolor: "hsl(0, 0%, 100%)",
        borderRadius: "1rem",
        boxShadow:
          "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
        p: { xs: "2rem", lg: "2.5rem" },
        display: "flex",
        flexDirection: "column",
        gap: "1.25rem",
      }}
    >
      {/* Title */}
      <Box
        component="h1"
        sx={{
          m: 0,
          fontFamily: "'Segoe UI Black', 'Segoe UI', system-ui, sans-serif",
          fontSize: { xs: "1.5rem", lg: "1.875rem" },
          lineHeight: 1.2,
          color: "hsl(222.2, 84%, 4.9%)",
        }}
      >
        <FormattedMessage
          id="devise.signup.tell_us_more_recruiter_html"
          values={{
            name: recruiterData.first_name,
            b: (chunks) => <b>{chunks}</b>,
          }}
        />
      </Box>

      {/* Form */}
      <form
        onSubmit={handleSubmit}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "0.75rem",
        }}
      >
        <TextField
          fullWidth
          type="text"
          name="name"
          variant="outlined"
          onBlur={handleInputValue}
          onChange={handleInputValue}
          sx={inputSx}
          {...(errors["name"] && {
            error: true,
            helperText: errors["name"],
          })}
          label={<FormattedMessage id="devise.signup.company_name" />}
        />

        <TextField
          fullWidth
          type="text"
          variant="outlined"
          onBlur={handleInputValue}
          onChange={handleInputValue}
          name="job_title"
          sx={inputSx}
          {...(errors["job_title"] && {
            error: true,
            helperText: errors["job_title"],
          })}
          label={<FormattedMessage id="devise.signup.job_title" />}
        />

        <FormControl fullWidth variant="outlined" sx={inputSx}>
          <InputLabel
            id="field-label"
            sx={{
              color: "hsl(220, 10%, 46%)",
              fontFamily: "Segoe UI Regular, system-ui, sans-serif",
              fontSize: "0.875rem",
              "&.Mui-focused": { color: "hsl(153 68% 25%)" },
            }}
          >
            <FormattedMessage id="devise.signup.company_industry" />
          </InputLabel>
          <Select
            id="type"
            label={`${intl.formatMessage({
              id: "devise.signup.company_industry",
            })}`}
            name="company_industry"
            value={values.company_industry}
            onChange={handleInputValue}
            sx={selectSx}
          >
            {companyIndustryCreateMap.map((value) => (
              <MenuItem value={value} key={value}>
                <FormattedMessage
                  id={"company.company_industry." + value}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {values.company_industry && (
          <FormControl fullWidth variant="outlined" sx={inputSx}>
            <InputLabel
              id="sub-sector-label"
              sx={{
                color: "hsl(220, 10%, 46%)",
                fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                fontSize: "0.875rem",
                "&.Mui-focused": { color: "hsl(153 68% 25%)" },
              }}
            >
              <FormattedMessage id="devise.signup.sub_sector" />
            </InputLabel>
            <Select
              id="sub-sector"
              label={`${intl.formatMessage({
                id: "devise.signup.sub_sector",
              })}`}
              name="sub_sector"
              value={values.sub_sector}
              onChange={handleInputValue}
              sx={selectSx}
            >
              {companySubSectorMap[values.company_industry]?.length > 0 && (
                companySubSectorMap[values.company_industry].map((sub) => (
                  <MenuItem value={sub} key={sub}>
                    <FormattedMessage id={"company.company_sub_sector." + sub} />
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        )}

        <TextField
          fullWidth
          name="location"
          variant="outlined"
          sx={inputSx}
          InputProps={{
            inputComponent: ({ ...props }) => (
              <Autocomplete
                /* eslint-disable no-undef */
                apiKey={process.env.GOOGLE_AUTOCOMPLETE_API_KEY}
                language="en"
                {...props}
                onPlaceSelected={onPlacesSelect}
                options={{
                  types: ["(regions)"],
                  componentRestrictions: {
                    country: ["be", "lu", "fr"],
                  },
                }}
                defaultValue={values.location}
                placeholder={intl.formatMessage({ id: "devise.signup.location" })}
              />
            ),
          }}
          {...(errors["location"] && {
            error: true,
            helperText: errors["location"],
          })}
        />

        {/* Submit */}
        <Button
          fullWidth
          type="submit"
          disabled={loading}
          sx={{
            bgcolor: "hsl(153 68% 25%)",
            color: "white",
            fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
            fontWeight: 700,
            fontSize: "0.875rem",
            px: "1.5rem",
            py: "0.75rem",
            borderRadius: "0.75rem",
            textTransform: "none",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.5rem",
            mt: "0.5rem",
            "&:hover": {
              bgcolor: "hsl(153 68% 25% / 0.9)",
            },
            "&.Mui-disabled": {
              bgcolor: "hsl(153 68% 25% / 0.3)",
              color: "hsl(0 0% 100% / 0.6)",
            },
          }}
        >
          {loading ? (
            <CircularProgress size={20} sx={{ color: "white" }} />
          ) : (
            <>
              <FormattedMessage id="devise.signup.build_team" />
              <ArrowRight size={14} />
            </>
          )}
        </Button>
      </form>

      {/* Footer link */}
      <Typography
        sx={{
          fontSize: "0.875rem",
          color: "hsl(215.4, 16.3%, 46.9%)",
          fontFamily: "'Segoe UI', system-ui, sans-serif",
        }}
      >
        <FormattedMessage id="devise.already_subscribed" />{" "}
        <Link
          href={`/${i18nLocale}/recruiters/sign_in`}
          sx={{
            color: "hsl(153, 68%, 25%)",
            fontWeight: 600,
            textDecoration: "none",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          <FormattedMessage id="signin" />
        </Link>
      </Typography>
    </Box>
  );
}
